import React, { useEffect, useRef, useState } from 'react';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { useLoadScript } from '@react-google-maps/api';
import { GOOGLE_MAPS_API_KEY } from 'app/utils/constants/paths';

// Mismo set que el resto de las páginas con mapa: el script de Google Maps se
// carga una sola vez por sesión, así que pedir menos librerías acá dejaría sin
// 'visualization' a quien entre primero por acá y después vaya a Clientes.
const GOOGLE_MAPS_LIBS = ['visualization', 'places'];
const MIN_QUERY_LENGTH = 3;
const SUGGESTIONS_DEBOUNCE_MS = 350;

// Buscador de direcciones con sugerencias de Google Places, acotado a Chile.
// Es freeSolo a propósito: la dirección igual se geocodifica en el backend, así
// que escribir algo que Places no sugiere sigue siendo válido.
const AddressAutocomplete = ({
    value = '',
    onChange,
    label = 'Dirección',
    helperText = '',
    placeholder = 'Escribe una dirección',
    size = 'small',
    fullWidth = true,
    disabled = false,
}) => {
    const { isLoaded } = useLoadScript({
        googleMapsApiKey: GOOGLE_MAPS_API_KEY,
        libraries: GOOGLE_MAPS_LIBS,
    });

    const [options, setOptions] = useState([]);
    const [loadingSuggestions, setLoadingSuggestions] = useState(false);
    const debounceTimerRef = useRef(null);
    const requestRef = useRef(0);
    const placesServiceRef = useRef(null);

    const clearPendingSearch = () => {
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
            debounceTimerRef.current = null;
        }
    };

    useEffect(() => clearPendingSearch, []);

    const getPlacesService = () => {
        if (placesServiceRef.current) return placesServiceRef.current;

        const ctor = window?.google?.maps?.places?.AutocompleteService;
        if (!ctor) return null;

        placesServiceRef.current = new ctor();
        return placesServiceRef.current;
    };

    const fetchSuggestions = (rawValue) => {
        const service = getPlacesService();
        if (!service) {
            setOptions([]);
            setLoadingSuggestions(false);
            return;
        }

        // Las respuestas pueden llegar desordenadas: sólo pinta la más reciente.
        const requestId = requestRef.current + 1;
        requestRef.current = requestId;
        setLoadingSuggestions(true);

        service.getPlacePredictions(
            {
                input: rawValue.trim(),
                componentRestrictions: { country: 'cl' },
                types: ['geocode'],
            },
            (predictions, status) => {
                if (requestId !== requestRef.current) return;

                const expectedStatus = window?.google?.maps?.places?.PlacesServiceStatus?.OK;
                setOptions(
                    predictions && status === expectedStatus
                        ? predictions.map((prediction) => prediction.description)
                        : []
                );
                setLoadingSuggestions(false);
            }
        );
    };

    const handleInputChange = (_event, nextValue, reason) => {
        onChange?.(nextValue || '');
        clearPendingSearch();

        if (reason === 'reset') return;

        if ((nextValue || '').trim().length < MIN_QUERY_LENGTH) {
            setOptions([]);
            return;
        }

        debounceTimerRef.current = setTimeout(() => fetchSuggestions(nextValue), SUGGESTIONS_DEBOUNCE_MS);
    };

    const resolvedHelperText = isLoaded
        ? helperText
        : 'Cargando el buscador de direcciones…';

    return (
        <Autocomplete
            freeSolo
            disabled={disabled}
            options={options}
            inputValue={value}
            onInputChange={handleInputChange}
            onChange={(_event, selectedValue, reason) => {
                if (reason !== 'selectOption' || typeof selectedValue !== 'string') return;

                clearPendingSearch();
                setOptions([]);
                onChange?.(selectedValue);
            }}
            loading={loadingSuggestions}
            filterOptions={(opts) => opts}
            noOptionsText={
                value.trim().length >= MIN_QUERY_LENGTH
                    ? 'Sin sugerencias'
                    : 'Escribe al menos 3 caracteres'
            }
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    size={size}
                    fullWidth={fullWidth}
                    placeholder={placeholder}
                    helperText={resolvedHelperText}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {loadingSuggestions ? <CircularProgress size={16} sx={{ mr: 0.5 }} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
        />
    );
};

export default AddressAutocomplete;
